import {
  Box,
  Flex,
  Spinner,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { IoLogoWhatsapp, IoSearchSharp } from 'react-icons/io5';
import { FiMail } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Pagination from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';
import { VoterDTO } from '../dtos';
import { useDebounce } from '../hooks/useDebounce';
import api from '../services/api';
import { convertDateFormat } from '../utils/convertDateFormat';
import { messagingPage } from '../utils/filterTables';
import AppShell from '../components/common/AppShell';
import EmptyState from '../components/common/EmptyState';
import ErrorState from '../components/common/ErrorState';
import LoadingSkeleton from '../components/common/LoadingSkeleton';
import PageHeader from '../components/common/PageHeader';
import SectionCard from '../components/common/SectionCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';

type MessagingData = {
  name: string;
  email: string;
  cellphone: string;
  id: string;
};

type BirthdayTemplateForm = {
  isActive: boolean;
  messageText: string;
  image?: string;
  sendTime: string;
  respectConsent: boolean;
};

type DeliveryChannel = 'WHATSAPP' | 'EMAIL' | 'BOTH';

export default function Messaging() {
  const toast = useToast();
  const [values, setValues] = useState({
    message: '',
    subject: '',
    voterMessages: [],
  } as {
    message: string;
    subject: string;
    voterMessages: string[];
  });
  const [imageFiles, setImageFiles] = useState<FileList | null>(null);
  const [base64Images, setBase64Images] = useState<string>();
  const [phonesToSendMessage, setPhonesToSendMessage] = useState<
    MessagingData[]
  >([]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterFieldDateMask, setFilterFieldDateMask] = useState('');
  const [data, setData] = useState([] as VoterDTO[]);
  const { office } = useAuth();
  const [selectFilter, setSelectFilter] = useState('name');
  const [filterField, setFilterField] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const debouncedValue = useDebounce(filterFieldDateMask || filterField, 500);
  const [selectedFile, setSelectedFile] = useState('');

  const perPage = 20;

  // Estado para o modal de QR Code
  const [isQrCodeModalOpen, setIsQrCodeModalOpen] = useState(false);
  const [qrCodeResponse, setQrCodeResponse] = useState<string>('');
  const [qrCodeValue, setQrCodeValue] = useState<string>(''); // guarda o value para montar o QR
  const [isQrLoading, setIsQrLoading] = useState(false);
  const [birthdayTemplate, setBirthdayTemplate] = useState<BirthdayTemplateForm>({
    isActive: false,
    messageText: 'Parabéns, {PRIMEIRO_NOME}! 🎉',
    sendTime: '09:00',
    respectConsent: true,
  });
  const [isLoadingBirthdayTemplate, setIsLoadingBirthdayTemplate] =
    useState(false);
  const [isSavingBirthdayTemplate, setIsSavingBirthdayTemplate] =
    useState(false);
  const [birthdayImageName, setBirthdayImageName] = useState('');
  const [deliveryChannel, setDeliveryChannel] =
    useState<DeliveryChannel>('WHATSAPP');
  const campaignFileInputRef = useRef<HTMLInputElement | null>(null);
  const birthdayFileInputRef = useRef<HTMLInputElement | null>(null);

  const openQrCodeModal = () => setIsQrCodeModalOpen(true);
  const closeQrCodeModal = () => setIsQrCodeModalOpen(false);

  const getBirthdayTemplate = async () => {
    if (!office?.id) {
      return;
    }

    try {
      setIsLoadingBirthdayTemplate(true);
      const response = await api.get('/admin/birthday-message/template', {
        params: {
          office_id: office.id,
        },
      });

      if (!response.data) {
        return;
      }

      setBirthdayTemplate({
        isActive: Boolean(response.data.is_active),
        messageText: response.data.message_text || 'Parabéns, {PRIMEIRO_NOME}! 🎉',
        image: response.data.image || undefined,
        sendTime: response.data.send_time || '09:00',
        respectConsent: Boolean(response.data.respect_consent),
      });
    } catch (error) {
      // Mantém valores padrão quando não existir template
    } finally {
      setIsLoadingBirthdayTemplate(false);
    }
  };

  const handleSaveBirthdayTemplate = async () => {
    if (!office?.id) {
      return;
    }

    try {
      setIsSavingBirthdayTemplate(true);
      await api.put('/admin/birthday-message/template', {
        office_id: office.id,
        isActive: birthdayTemplate.isActive,
        messageText: birthdayTemplate.messageText,
        image: birthdayTemplate.image,
        sendTime: birthdayTemplate.sendTime,
        respectConsent: birthdayTemplate.respectConsent,
      });

      toast({
        title: 'Template de aniversário salvo',
        status: 'success',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title:
          error?.response?.data?.message ||
          'Erro ao salvar template de aniversário.',
        status: 'error',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingBirthdayTemplate(false);
    }
  };

  // GET /whatsapp/qrcode e exibir no modal (QR + JSON)
  const getWhatsappQrCode = async () => {
    try {
      setIsQrLoading(true);
      const response = await api.get('/whatsapp/qrcode');

      const objectResponse = JSON.parse(response.data);

      // a API já retorna { value: "2@..." }
      const value = objectResponse?.value || '';

      setQrCodeValue(value);
      setWhatsappConnected(objectResponse?.connected || false);

      // se quiser mostrar o objeto bruto no <pre>, ok:
      setQrCodeResponse(JSON.stringify(response.data, null, 2));

      openQrCodeModal();
    } catch (error) {
      toast({
        title: 'Erro ao gerar QR Code',
        status: 'error',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsQrLoading(false);
    }
  };

  const getVoterList = async (currentPage = 1) => {
    setData([] as VoterDTO[]);
    setLoading(true);
    setHasError(false);
    try {
      const filterMapping = {
        voter: 'voter.name',
        creator: 'creator.name',
        city: 'city',
        neighborhood: 'neighborhood',
      };

      const currentFilter =
        filterMapping[selectFilter as keyof typeof filterMapping] ||
        selectFilter;

      const response = await api.get(`/voter/office/${office.id}`, {
        params: {
          page: currentPage,
          quantity: perPage,
          field: currentFilter,
          value: filterFieldDateMask
            ? convertDateFormat(filterFieldDateMask)
            : filterField,
        },
      });

      setData(response.data.items);
      setTotalPages(response.data.total);
    } catch (err) {
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    setSelectedFile(file.name);

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = () => {
      const base64String = reader.result as string;
      setBase64Images(base64String);
    };

    reader.onerror = (error) => {
      console.error('Erro ao converter imagem para Base64:', error);
      toast({
        title: 'Erro ao processar imagem',
        description: 'Não foi possível converter a imagem para Base64.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    };
  };

  const handleBirthdayImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    setBirthdayImageName(file.name);

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = () => {
      const base64String = reader.result as string;
      setBirthdayTemplate((prev) => ({
        ...prev,
        image: base64String,
      }));
    };

    reader.onerror = (error) => {
      console.error('Erro ao converter imagem do aniversário para Base64:', error);
      toast({
        title: 'Erro ao processar imagem',
        description: 'Não foi possível converter a imagem do aniversário.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    };
  };

  const handleSendMessage = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const channels =
        deliveryChannel === 'BOTH'
          ? ['WHATSAPP', 'EMAIL']
          : [deliveryChannel];

      if (channels.includes('EMAIL') && !values.subject.trim()) {
        toast({
          title: 'Informe o assunto do e-mail',
          status: 'warning',
          position: 'top-right',
          duration: 3000,
          isClosable: true,
        });
        setIsSubmitting(false);
        return;
      }

      const response = await api.post(`/whatsapp/dispatch-bulk`, {
        voters: phonesToSendMessage,
        message: values.message,
        subject: values.subject,
        image: base64Images,
        channels,
      });

      const summary = response.data;

      setValues({ ...values, subject: '', voterMessages: [] });
      setPhonesToSendMessage([]);
      setImageFiles(null);
      setIsAllChecked(false);

      toast({
        title: 'Mensagem enviada',
        description: `WhatsApp: ${summary?.whatsapp?.queued || 0} enviado(s), ${summary?.whatsapp?.skipped || 0} ignorado(s). E-mail: ${summary?.email?.sent || 0} enviado(s), ${summary?.email?.skipped || 0} ignorado(s).`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title:
          'Ocorreu um erro ao enviar sua mensagem, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const fetchAllVoterPhones = async (): Promise<MessagingData[]> => {
    let allPhones: MessagingData[] = [];

    try {
      const response = await api.get<{ items: VoterDTO[]; totalPages: number }>(
        `/voter/office/${office.id}`,
        {
          params: {
            page: 1,
            quantity: 10000,
            field: selectFilter,
            value: filterFieldDateMask
              ? convertDateFormat(filterFieldDateMask)
              : filterField,
          },
        }
      );

      allPhones = response.data.items.map((voter: VoterDTO) => ({
        cellphone: voter.cellphone,
        name: voter.name,
        email: voter.email,
        id: voter.id,
      }));
    } catch (error) {
      console.error('Error fetching voter phones:', error);
    }

    return allPhones;
  };

  const handleCheckAllChange = async (isChecked: boolean) => {
    setIsAllChecked(isChecked);

    if (isChecked) {
      setLoading(true);
      const allPhones = data.map((item) => ({
        name: item.name,
        email: item.email,
        cellphone: item.cellphone,
        id: item.id,
      }));
      setPhonesToSendMessage(allPhones);
      setLoading(false);
    } else {
      setPhonesToSendMessage([]);
    }
  };

  const handleDateOfBirthChange = (input: any) => {
    const dateRegEx = /^(\d{1,2})(\/)?(\d{1,2})?(\d{0,4})?$/;

    const match = input.match(dateRegEx);

    if (match) {
      let formattedDate = match[1];

      if (match[3]) {
        formattedDate += '/' + match[3];
      }

      if (match[4]) {
        formattedDate += '/' + match[4];
      }

      setFilterFieldDateMask(formattedDate);
    } else {
      setFilterFieldDateMask(input);
    }

    setFilterField(input);
  };

  const handleCheckboxChange = (voter: VoterDTO, isChecked: boolean) => {
    setPhonesToSendMessage((prev) => {
      const exists = prev.some((p) => p.id === voter.id);

      if (isChecked && !exists) {
        const entry: MessagingData = {
          id: voter.id,
          name: voter.name,
          email: voter.email,
          cellphone: voter.cellphone,
        };
        return [...prev, entry];
      }

      if (!isChecked && exists) {
        return prev.filter((p) => p.id !== voter.id);
      }

      return prev;
    });
  };

  useEffect(() => {
    getVoterList(page);
    setIsAllChecked(false);
  }, [page, debouncedValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedValue]);

  useEffect(() => {
    setFilterField('');
    setFilterFieldDateMask('');
  }, [selectFilter]);

  useEffect(() => {
    getBirthdayTemplate();
  }, [office?.id]);

  const selectedStats = {
    total: phonesToSendMessage.length,
    whatsapp: phonesToSendMessage.filter((item) => item.cellphone).length,
    email: phonesToSendMessage.filter((item) => item.email).length,
  };

  return (
    <AppShell>
      {!office?.messaging_enabled ? (
        <SectionCard title="Mensageria">
          <EmptyState
            title="Mensageria desabilitada"
            description="Este gabinete não possui a tela de mensageria liberada."
          />
        </SectionCard>
      ) : (
        <>
      <PageHeader
        title="Mensageria"
        description="Selecione os apoiadores e envie campanhas por WhatsApp, e-mail ou pelos dois canais."
        actions={
          <>
            <Button
              variant="outline"
              className="min-w-36"
              onClick={getWhatsappQrCode}
              disabled={isQrLoading}
              style={{ borderColor: office?.primary_color, color: office?.primary_color }}
            >
              {isQrLoading ? <Spinner size="sm" /> : 'Gerar QR Code'}
            </Button>
            <Button
              className="min-w-40"
              onClick={handleSendMessage}
              disabled={
                isSubmitting ||
                phonesToSendMessage.length === 0 ||
                values.message.trim() === ''
              }
              style={{ backgroundColor: office?.primary_color, color: office?.secondary_color }}
            >
              {isSubmitting ? <Spinner color="white" size="sm" /> : 'Enviar campanha'}
            </Button>
          </>
        }
      />

      <SectionCard
        title="Mensagem de Aniversário Automática"
        description="Configure o texto que será enviado automaticamente para aniversariantes."
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Texto da mensagem
            </label>
            <p className="text-xs text-slate-500">
              Placeholders disponíveis: {'{NOME}'}, {'{PRIMEIRO_NOME}'},{' '}
              {'{IDADE}'}, {'{{nome}}'}, {'{{primeiro_nome}}'}, {'{{idade}}'}
            </p>
            <Textarea
              value={birthdayTemplate.messageText}
              onChange={(e) =>
                setBirthdayTemplate((prev) => ({
                  ...prev,
                  messageText: e.target.value,
                }))
              }
              placeholder="Parabéns, {PRIMEIRO_NOME}! 🎉"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Imagem do WhatsApp
            </label>
            <p className="text-xs text-slate-500">
              Se definida, a imagem sera enviada antes da mensagem de aniversario.
            </p>
            <input
              ref={birthdayFileInputRef}
              type="file"
              accept="image/*"
              onChange={handleBirthdayImageChange}
              id="birthday-file-upload"
              style={{ display: 'none' }}
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                className="cursor-pointer"
                onClick={() => birthdayFileInputRef.current?.click()}
              >
                Carregar imagem
              </Button>
              {birthdayTemplate.image ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setBirthdayTemplate((prev) => ({ ...prev, image: undefined }));
                    setBirthdayImageName('');
                  }}
                >
                  Remover imagem
                </Button>
              ) : null}
              {birthdayImageName ? (
                <span className="text-sm text-slate-600">{birthdayImageName}</span>
              ) : birthdayTemplate.image ? (
                <span className="text-sm text-slate-600">Imagem salva no template</span>
              ) : null}
            </div>
            {birthdayTemplate.image ? (
              <Box mt={2}>
                <img
                  src={birthdayTemplate.image}
                  alt="Preview da imagem de aniversário"
                  style={{ maxWidth: '200px', borderRadius: '8px' }}
                />
              </Box>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Horário do envio
              </label>
              <Input
                type="time"
                value={birthdayTemplate.sendTime}
                onChange={(e) =>
                  setBirthdayTemplate((prev) => ({
                    ...prev,
                    sendTime: e.target.value,
                  }))
                }
              />
            </div>

            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 md:mt-8">
              <input
                type="checkbox"
                checked={birthdayTemplate.isActive}
                onChange={(e) =>
                  setBirthdayTemplate((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
              />
              Ativar template
            </label>

            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 md:mt-8">
              <input
                type="checkbox"
                checked={birthdayTemplate.respectConsent}
                onChange={(e) =>
                  setBirthdayTemplate((prev) => ({
                    ...prev,
                    respectConsent: e.target.checked,
                  }))
                }
              />
              Respeitar consentimento
            </label>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSaveBirthdayTemplate}
              disabled={isSavingBirthdayTemplate || isLoadingBirthdayTemplate}
              style={{
                backgroundColor: office?.primary_color,
                color: office?.secondary_color,
              }}
            >
              {isSavingBirthdayTemplate || isLoadingBirthdayTemplate ? (
                <Spinner size="sm" />
              ) : (
                'Salvar template de aniversário'
              )}
            </Button>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Filtros e mensagem"
        description="Use filtros para selecionar apoiadores e personalize o texto do disparo."
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[220px_220px_1fr]">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Canal</label>
              <Select
                value={deliveryChannel}
                onChange={(e) => setDeliveryChannel(e.target.value as DeliveryChannel)}
              >
                <option value="WHATSAPP">WhatsApp</option>
                <option value="EMAIL">E-mail</option>
                <option value="BOTH">WhatsApp + E-mail</option>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Destinatários selecionados
              </label>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {selectedStats.total} selecionado(s) • {selectedStats.whatsapp} com
                WhatsApp • {selectedStats.email} com e-mail
              </div>
            </div>

            {(deliveryChannel === 'EMAIL' || deliveryChannel === 'BOTH') ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Assunto do e-mail
                </label>
                <Input
                  type="text"
                  value={values.subject}
                  onChange={(e) =>
                    setValues({ ...values, subject: e.target.value })
                  }
                  placeholder="Ex.: Comunicado do gabinete"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Cobertura do canal</label>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {deliveryChannel === 'WHATSAPP'
                    ? `${selectedStats.whatsapp} contato(s) podem receber por WhatsApp`
                    : `${selectedStats.email} contato(s) podem receber por e-mail`}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[220px_1fr_auto]">
            <Select
              name="filterType"
              value={selectFilter}
              onChange={(e) => {
                setSelectFilter(e.target.value);
              }}
            >
              {messagingPage.map((voter) => {
                return (
                  <option key={voter?.key} value={voter?.value}>
                    {voter?.label}
                  </option>
                );
              })}
            </Select>

            <div className="relative">
              {selectFilter === 'birthdate' ? (
                <Input
                  name="filterField"
                  type="tel"
                  inputMode="numeric"
                  onKeyPress={(e) => {
                    if (!/\d/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Buscar"
                  value={filterFieldDateMask}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    handleDateOfBirthChange(inputValue);
                  }}
                  pattern="\d*"
                  className="pr-10"
                />
              ) : (
                <Input
                  type="text"
                  name="filterField"
                  placeholder="Buscar"
                  value={filterField}
                  onChange={(e) => {
                    setFilterField(e.target.value);
                  }}
                  className="pr-10"
                />
              )}
              <IoSearchSharp className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-slate-400" />
            </div>

            <Button
              variant="destructive"
              onClick={() => {
                setValues({ ...values, message: '', subject: '', voterMessages: [] });
                setPhonesToSendMessage([]);
                setIsAllChecked(false);
                setBase64Images(undefined);
                setImageFiles(null);
                setSelectedFile('');
              }}
              disabled={phonesToSendMessage.length === 0 && values.message === ''}
              className="whitespace-nowrap"
            >
              Remover alterações
            </Button>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Mensagem</label>
            <p className="text-xs text-slate-500">
              {'Você pode usar {{nome}} ou {{primeiro_nome}} para personalizar a mensagem.'}
            </p>
            <Textarea
              value={values.message}
              onChange={(e) => setValues({ ...values, message: e.target.value })}
              placeholder={
                deliveryChannel === 'EMAIL'
                  ? 'Escreva o conteúdo do e-mail'
                  : 'Deixe aqui sua mensagem'
              }
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <IoLogoWhatsapp className="h-4 w-4 text-green-600" />
              <FiMail className="h-4 w-4 text-slate-500" />
              <span>
                A mesma imagem pode acompanhar o WhatsApp e aparecer no corpo do e-mail.
              </span>
            </div>
            <input
              ref={campaignFileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              id="file-upload"
              style={{ display: 'none' }}
            />
            <Button
              type="button"
              variant="secondary"
              className="cursor-pointer"
              onClick={() => campaignFileInputRef.current?.click()}
            >
              <span>Carregar imagem</span>
            </Button>
            {selectedFile ? <p className="text-sm text-slate-600">{selectedFile}</p> : null}
            {base64Images ? (
              <Box mt={2}>
                <img
                  src={base64Images}
                  alt="Preview"
                  style={{ maxWidth: '200px', borderRadius: '8px' }}
                />
              </Box>
            ) : null}
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Destinatários">
        {loading ? <LoadingSkeleton rows={8} /> : null}

        {!loading && hasError ? <ErrorState onRetry={() => getVoterList(page)} /> : null}

        {!loading && !hasError && data.length === 0 ? (
          <EmptyState title="Nenhum dado cadastrado" description="Ajuste os filtros para encontrar apoiadores." />
        ) : null}

        {!loading && !hasError && data.length > 0 ? (
          <div className="space-y-4">
            <div className="max-h-[500px] overflow-auto rounded-md border">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-white">
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        aria-label="Selecionar todos os apoiadores"
                        type="checkbox"
                        checked={isAllChecked}
                        onChange={(e) => handleCheckAllChange(e.target.checked)}
                      />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>E-mail</TableHead>
                    <TableHead>Endereço</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((voter) => {
                    return (
                      <TableRow key={voter.id}>
                        <TableCell>
                          <input
                            aria-label={`Selecionar ${voter?.name}`}
                            type="checkbox"
                            checked={phonesToSendMessage.some((item) => item?.id === voter?.id)}
                            onChange={(e) => {
                              return handleCheckboxChange(voter, e.target.checked);
                            }}
                          />
                        </TableCell>
                        <TableCell>{voter?.name}</TableCell>
                        <TableCell>
                          {voter?.cellphone ? (
                            <Link
                              target="__blank"
                              to={`https://wa.me/55${voter?.cellphone}`}
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900"
                            >
                              <IoLogoWhatsapp
                                aria-label="Abrir WhatsApp do contato"
                                className="h-5 w-5"
                                style={{ color: office?.primary_color }}
                              />
                              {voter?.cellphone}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {voter?.email ? (
                            <span className="inline-flex items-center gap-2 text-sm text-slate-700">
                              <FiMail className="h-4 w-4 text-slate-500" />
                              {voter?.email}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="max-w-[380px] whitespace-normal">
                          {voter?.street
                            ? voter?.zip
                              ? `${voter?.street ? voter?.street + ',' : ''}
                                 ${voter?.address_number ? voter?.address_number + ',' : ''}
                                 ${voter?.neighborhood ? voter?.neighborhood + ',' : ''}
                                 ${voter?.complement ? voter?.complement + ',' : ''}
                                 ${voter?.city ? voter?.city + ',' : ''}
                                 ${voter?.state ? voter?.state + ',' : ''}`
                              : '-'
                            : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <Flex alignItems="center" justifyContent="center">
              <Pagination
                currentPage={page}
                perPage={perPage}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </Flex>
          </div>
        ) : null}
      </SectionCard>

      {/* Modal do QR Code: primeiro o QR code, abaixo o JSON */}
      <Modal isOpen={isQrCodeModalOpen} onClose={closeQrCodeModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Resposta do QR Code</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {whatsappConnected && (
              <Text mb="2px" color="green.500" fontSize="12px">
                Whatsapp já está conectado!
              </Text>
            )}
            {/* QR Code (se houver valor) */}
            {qrCodeValue && (
              <Flex justifyContent="center" mb={4}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                    qrCodeValue
                  )}`}
                  alt="QR Code"
                />
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
        </>
      )}
    </AppShell>
  );
}
